import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { KalDialogRef } from '@kalidea/kaligraphi';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import {
    AdvancedRuleService,
    ComputationNodeType,
    RuleComputation,
    TypesOperateur
} from 'src/app/_shared/services/advanced-rule/advanced-rule.service';
import { FormValidator } from 'src/app/validators/form-validator';

export interface SharedAdvancedRuleLinearFormulaDialogData {
    replaceCurrentComputations?: boolean;

    computationList?: RuleComputation[];

    accepted?: boolean;
}

type linearFormulaRow = {
    label: string,
    valueControlName: string,
    subventionControlName: string,
};

@Component({
    selector: 'app-shared-advanced-rule-linear-formula-dialog',
    templateUrl: './shared-advanced-rule-linear-formula-dialog.component.html',
    styleUrls: ['./shared-advanced-rule-linear-formula-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAdvancedRuleLinearFormulaDialogComponent implements OnInit {

    readonly criteriaList = this.advancedRuleService.criteriaOfCriteriaList;

    readonly operatorList = this.advancedRuleService.operatorList;

    form: UntypedFormGroup;

    readonly columns: KalTableHeader<linearFormulaRow>[] = [
        {code: 'label', label: ''},
        {code: 'valueControlName', label: 'Valeur critère (X)'},
        {code: 'subventionControlName', label: 'Valeur critère (Y)'}
    ];

    readonly dataSource = [
        {
            label: 'Borne min. (1)',
            valueControlName: 'valueControlNameMin',
            subventionControlName: 'subventionControlNameMin',
        },
        {
            label: 'Borne max. (2)',
            valueControlName: 'valueControlNameMax',
            subventionControlName: 'subventionControlNameMax',
        },
    ];

    constructor(private readonly fb: UntypedFormBuilder,
                private readonly advancedRuleService: AdvancedRuleService,
                private readonly dialogRef: KalDialogRef<SharedAdvancedRuleLinearFormulaDialogComponent>) {
    }

    generateLinearFormulaComputations(): RuleComputation[] {
        const {
            criterion,
            valueControlNameMin,
            subventionControlNameMin,
            valueControlNameMax,
            subventionControlNameMax,
        } = this.form.value;

        const multiply = this.getOperatorFromLabel('*');
        const divide = this.getOperatorFromLabel('/');
        const substract = this.getOperatorFromLabel('-');
        const add = this.getOperatorFromLabel('+');
        const min = this.getOperatorFromLabel('Minimum');
        const max = this.getOperatorFromLabel('Maximum');
        const openParenthesis = this.createOperator('(', TypesOperateur.Arithmetique);
        const closeParenthesis = this.createOperator(')', TypesOperateur.Arithmetique);
        const subventionMax = this.valueToValueNode(subventionControlNameMax);
        const subventionMin = this.valueToValueNode(subventionControlNameMin);
        const valueMin = this.valueToValueNode(valueControlNameMin);
        const valueMax = this.valueToValueNode(valueControlNameMax);


        return [
            this.getCriterionByLabel('Prix de vente de l\'article'),
            multiply,
            openParenthesis,
            openParenthesis,
            {Critere: criterion, type: ComputationNodeType.Critere},
            multiply,
            openParenthesis,
            openParenthesis,
            subventionMax,
            substract,
            subventionMin,
            closeParenthesis,
            divide,
            openParenthesis,
            valueMax,
            substract,
            valueMin,
            closeParenthesis,
            closeParenthesis,
            add,
            openParenthesis,
            subventionMin,
            substract,
            openParenthesis,
            openParenthesis,
            openParenthesis,
            subventionMax,
            substract,
            subventionMin,
            closeParenthesis,
            divide,
            openParenthesis,
            valueMax,
            substract,
            valueMin,
            closeParenthesis,
            closeParenthesis,
            multiply,
            valueMin,
            closeParenthesis,
            closeParenthesis,
            closeParenthesis,
            min,
            subventionMin,
            max,
            subventionMax,
            closeParenthesis,
            divide,
            this.valueToValueNode(100)
        ];
    }

    submit(): void {
        const {replaceCurrentComputations,} = this.form.value;

        this.dialogRef.close({
            accepted: true,
            replaceCurrentComputations,
            computationList: this.generateLinearFormulaComputations()
        });
    }

    close(): void {
        this.dialogRef.close({
            accepted: false
        });
    }

    private valueToValueNode(value: number): RuleComputation {
        return {type: ComputationNodeType.Valeur, Valeur: value};
    }

    private createOperator(value: string, operatorType: TypesOperateur): RuleComputation {
        return {
            Operateur: {
                Id: 0,
                Libelle: value,
                Valeur: value,
                TypeOperateur: operatorType
            },
            type: ComputationNodeType.Operateur
        };
    }

    private getCriterionByLabel(label: string): RuleComputation {
        return {
            Critere: this.criteriaList.find(({Libelle}) => Libelle === label),
            type: ComputationNodeType.Critere
        };
    }

    private getOperatorFromLabel(label: string): RuleComputation {
        return {
            Operateur: this.operatorList.find(({Libelle}) => Libelle === label),
            type: ComputationNodeType.Operateur
        };
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            criterion: [null, [FormValidator.isNotNull]],
            valueControlNameMin: [0, [FormValidator.isNotNull, Validators.min(0)]],
            subventionControlNameMin: [0, [FormValidator.numberRangeValidator(0, 100), FormValidator.isNotNull]],
            valueControlNameMax: [0, [Validators.min(1), FormValidator.isNotNull]],
            subventionControlNameMax: [0, [FormValidator.numberRangeValidator(0, 100), FormValidator.isNotNull]],
            replaceCurrentComputations: [true],
        }, {
            validators: [
                FormValidator.overNumber('valueControlNameMin', 'valueControlNameMax')
            ]
        });
    }

}
